// src/components/admin/ImageUploadPopup.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FiSave, FiX, FiImage } from 'react-icons/fi';

const ImageUploadPopup = ({
  isOpen,
  onClose,
  onSave,
  title,
  type,
  currentImageUrl,
}) => {
  const [imageUrl, setImageUrl] = useState(currentImageUrl || '');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!imageUrl.trim()) {
      alert('Please enter an image URL');
      return;
    }

    if (imageUrl.startsWith('blob:')) {
      alert('Invalid image URL. Please use a valid image URL (https://...)');
      return;
    }

    try {
      new URL(imageUrl);
    } catch (e) {
      alert('Please enter a valid URL (e.g., https://example.com/image.jpg)');
      return;
    }

    setLoading(true);
    await onSave(imageUrl);
    setLoading(false);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
    >
      <div
        className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
        style={{
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
              {title}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
            </button>
          </div>

          {/* Preview */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Preview
            </label>
            <div
              className="relative overflow-hidden rounded-xl"
              style={{
                backgroundColor: 'var(--border)',
                aspectRatio: type === 'cover' ? '3/1' : '1/1',
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={e => {
                    e.target.src = '';
                    e.target.style.backgroundColor = 'var(--border)';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FiImage
                    className="w-12 h-12"
                    style={{ color: 'var(--muted)' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Guidelines */}
          {type === 'cover' && (
            <div
              className="mb-4 p-3 rounded-lg text-xs space-y-1"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="font-semibold mb-1"
                style={{ color: 'var(--text)' }}
              >
                📐 Cover Image Guidelines:
              </p>
              <ul className="space-y-1" style={{ color: 'var(--muted)' }}>
                <li>
                  • Recommended ratio:{' '}
                  <strong className="font-semibold">3:1</strong> (1500x500px)
                </li>
                <li>
                  • Minimum width: <strong>1200px</strong>
                </li>
                <li>
                  • Maximum file size: <strong>5MB</strong>
                </li>
                <li>• Formats: JPG, PNG, WebP</li>
              </ul>
            </div>
          )}

          {type === 'profile' && (
            <div
              className="mb-4 p-3 rounded-lg text-xs space-y-1"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="font-semibold mb-1"
                style={{ color: 'var(--text)' }}
              >
                📷 Profile Image Guidelines:
              </p>
              <ul className="space-y-1" style={{ color: 'var(--muted)' }}>
                <li>
                  • Recommended ratio: <strong>1:1</strong> (square)
                </li>
                <li>
                  • Recommended size: <strong>400x400px</strong>
                </li>
                <li>
                  • Maximum file size: <strong>2MB</strong>
                </li>
                <li>• Formats: JPG, PNG, WebP</li>
              </ul>
            </div>
          )}

          {/* Image URL Input */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Image URL
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-2 rounded-xl outline-none transition-all"
              style={{
                backgroundColor: 'var(--border)',
                color: 'var(--text)',
                border: '1px solid transparent',
              }}
              onFocus={e => (e.currentTarget.style.borderColor = 'var(--text)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'transparent')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-xl transition-all"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
            >
              {loading ? 'Saving...' : 'Save Image'}{' '}
              <FiSave className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageUploadPopup;
